import React, { useMemo } from 'react';
import InfoTooltip from '../InfoTooltip';
import { useNumberLocale } from '../../../utils/numberFormat';
import { useChartFormatters, getYearEndTicks, formatYearTickLabel } from '../chartFormatters';
import TailwindBarChart from '../../Tailwind/TailwindBarChart';
import TailwindLineChart from '../../Tailwind/TailwindLineChart';
import { useChartPalette } from '../../Tailwind/chartColors';

function SummaryCard({
    label,
    value,
    icon,
    info,
}: {
    label: string;
    value: number;
    icon: string;
    info?: React.ReactNode;
}) {
    const numberLocale = useNumberLocale();
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-[18px] text-slate-400">{icon}</span>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
                {info && <InfoTooltip content={info} />}
            </div>
            <div className="text-2xl font-bold text-slate-900">
                {value?.toLocaleString(numberLocale, { maximumFractionDigits: 0 }) || '—'}
            </div>
        </div>
    );
}

const FUNDING_INFO: Record<string, React.ReactNode> = {
    total_commitment: (
        <>
            <strong>Total Commitment</strong> — Total capital pledged across the portfolio.
            <br />
            <em>Formula:</em> Σ (commitment × equity_percentage) across assets.
            <br />
            Per-asset commitment is the user-entered override if set, otherwise inferred as the sum of all drawdowns (common equity + SHL).
        </>
    ),
    total_drawn: (
        <>
            <strong>Total Drawn</strong> — Capital already called and deployed.
            <br />
            <em>Formula:</em> Σ aggregated drawdowns = Σ (common_share_drawdown + shareholder_loan_drawdown), pro-rated by equity_percentage.
        </>
    ),
    unfunded: (
        <>
            <strong>Unfunded</strong> — "Dry powder" remaining: committed capital not yet called.
            <br />
            <em>Formula:</em> Total Commitment − Total Drawn.
        </>
    ),
    peak_exposure: (
        <>
            <strong>Peak Exposure</strong> — Maximum equity at risk.
            <br />
            Maximum amount of unreturned capital outstanding at any point in time. Measures peak cash funding need.
            <br />
            <em>Formula:</em> max( cumsum(Drawdowns) − cumsum(Distributions) ) over the full horizon.
        </>
    ),
};

export default function FundingTab({ data }: { data: any }) {
    const numberLocale = useNumberLocale();
    const { formatYAxisTick, formatTooltipValue } = useChartFormatters();
    const palette = useChartPalette();

    const drawdownChartData = useMemo(() => {
        const ts = data.drawdown_schedule_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            drawdown: value,
        }));
    }, [data.drawdown_schedule_ts]);

    const cumulativeChartData = useMemo(() => {
        const ts = data.cumulative_drawdown_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            cumulative: value,
        }));
    }, [data.cumulative_drawdown_ts]);

    const drawdownTicks = useMemo(() => getYearEndTicks(drawdownChartData.map(d => d.date)), [drawdownChartData]);
    const cumulativeTicks = useMemo(() => getYearEndTicks(cumulativeChartData.map(d => d.date)), [cumulativeChartData]);

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard label="Total Commitment" value={data.total_commitment} icon="account_balance" info={FUNDING_INFO.total_commitment} />
                <SummaryCard label="Total Drawn" value={data.total_drawn} icon="south_west" info={FUNDING_INFO.total_drawn} />
                <SummaryCard label="Unfunded" value={data.unfunded_commitment} icon="pending" info={FUNDING_INFO.unfunded} />
                <SummaryCard label="Peak Exposure" value={data.peak_equity_exposure} icon="vertical_align_top" info={FUNDING_INFO.peak_exposure} />
            </div>

            {/* Drawdown Schedule Chart */}
            {drawdownChartData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Equity Drawdown Schedule</h3>
                    <TailwindBarChart
                        data={drawdownChartData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'drawdown',
                            label: 'Drawdown',
                            color: palette[0],
                            radius: [2, 2, 0, 0],
                        }]}
                        xAxisTicks={drawdownTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* Cumulative Drawdown Line Chart */}
            {cumulativeChartData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Cumulative Capital Calls</h3>
                    <TailwindLineChart
                        data={cumulativeChartData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'cumulative',
                            label: 'Cumulative Drawdowns',
                            color: palette[0],
                            strokeWidth: 2,
                            showDots: false,
                        }]}
                        xAxisTicks={cumulativeTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* Capital Call Breakdown per Asset */}
            {data.capital_call_breakdown && data.capital_call_breakdown.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Capital Call Breakdown by Asset</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Asset</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Common Equity</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">SHL</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.capital_call_breakdown.map((item: any, i: number) => (
                                    <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-2.5 font-medium text-slate-900">
                                            {item.model_name} {item.company_name ? `- ${item.company_name}` : ''}
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-slate-700">{item.common_equity_total?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-700">{item.shl_total?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{item.total?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Quarterly Summary Table — funding view */}
            {data.quarterly_summaries && data.quarterly_summaries.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Quarterly Summary</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Quarter</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Drawdowns</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Cumul. Drawn</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Unfunded</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Assets</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.quarterly_summaries.map((q: any, i: number) => (
                                    <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-2 font-medium text-slate-900">{q.quarter}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.drawdowns?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.cumulative_drawdowns?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.unfunded_commitment?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.num_assets_active}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
